import type { SupabaseClient } from '@supabase/supabase-js';
import { SEASON_TABLE_NAME } from '@/consts/season';
import type {
  Database,
  RankingEntry,
  UserGameStats,
  UserGameStatsUpdate,
} from '@/types/UserGameStats';
import { NotFoundError, ValidationError, handleSupabaseError } from './errors';
import { createUserProfile } from './profile';

export type SaveGameResultResponse = {
  highScore: number;
  isNewHighScore: boolean;
};

export async function getUserStats(
  supabase: SupabaseClient<Database>,
  userId: string,
): Promise<UserGameStats | null> {
  if (!userId) throw new ValidationError('ユーザーIDが指定されていません');
  const { data, error } = await supabase
    .from(SEASON_TABLE_NAME)
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) handleSupabaseError(error);
  return data;
}

export async function createUserStats(
  supabase: SupabaseClient<Database>,
  userId: string,
): Promise<UserGameStats> {
  if (!userId) throw new ValidationError('ユーザーIDが指定されていません');
  const existing = await getUserStats(supabase, userId);
  if (existing) return existing;

  await createUserProfile(supabase, userId);

  const { data, error } = await supabase
    .from(SEASON_TABLE_NAME)
    .insert({
      user_id: userId,
      high_score: 0,
      block_01: 0,
      block_02: 0,
      block_03: 0,
      block_04: 0,
      block_05: 0,
      block_06: 0,
      block_07: 0,
      block_08: 0,
      block_09: 0,
      block_10: 0,
    })
    .select()
    .single();

  if (error) {
    if (error.code === '23505') {
      const existingAfterConflict = await getUserStats(supabase, userId);
      if (existingAfterConflict) return existingAfterConflict;
    }
    handleSupabaseError(error);
  }
  if (!data) throw new NotFoundError('統計データの作成に失敗しました');
  return data;
}

export async function saveGameResult(
  supabase: SupabaseClient<Database>,
  userId: string,
  newScore: number,
  blockCounts: Record<string, number>,
): Promise<SaveGameResultResponse> {
  if (!userId) throw new ValidationError('ユーザーIDが指定されていません');
  if (!Number.isFinite(newScore) || newScore < 0) {
    throw new ValidationError('スコアは0以上の数値である必要があります');
  }

  let currentStats = await getUserStats(supabase, userId);
  if (!currentStats) currentStats = await createUserStats(supabase, userId);

  if (newScore <= currentStats.high_score) {
    return { highScore: currentStats.high_score, isNewHighScore: false };
  }

  const allowedBlockKeys = new Set(
    Array.from({ length: 10 }, (_, index) =>
      `block_${String(index + 1).padStart(2, '0')}`,
    ),
  );
  const safeBlockCounts = Object.fromEntries(
    Object.entries(blockCounts).filter(
      ([key, value]) =>
        allowedBlockKeys.has(key) && Number.isFinite(value) && value >= 0,
    ),
  ) as UserGameStatsUpdate;

  const { data, error } = await supabase
    .from(SEASON_TABLE_NAME)
    .update({ high_score: newScore, ...safeBlockCounts })
    .eq('user_id', userId)
    .select('high_score')
    .single();
  if (error) handleSupabaseError(error);

  return {
    highScore: data?.high_score ?? newScore,
    isNewHighScore: true,
  };
}

export async function getTopScores(
  supabase: SupabaseClient<Database>,
  limit = 10,
): Promise<RankingEntry[]> {
  if (!Number.isInteger(limit) || limit < 1) {
    throw new ValidationError('limitは1以上の整数である必要があります');
  }

  const { data: scores, error: scoresError } = await supabase
    .from(SEASON_TABLE_NAME)
    .select('user_id, high_score')
    .order('high_score', { ascending: false })
    .limit(limit);
  if (scoresError) handleSupabaseError(scoresError);
  if (!scores?.length) return [];

  const userIds = scores.map((entry) => entry.user_id);
  const { data: profiles, error: profilesError } = await supabase
    .from('user_profiles')
    .select('user_id, user_name')
    .in('user_id', userIds);
  if (profilesError) handleSupabaseError(profilesError);

  const userNameById = new Map(
    (profiles ?? []).map((profile) => [profile.user_id, profile.user_name]),
  );

  return scores
    .map((entry) => ({
      user_id: entry.user_id,
      high_score: entry.high_score,
      user_name: userNameById.get(entry.user_id),
    }))
    .filter(
      (entry): entry is { user_id: string; high_score: number; user_name: string } =>
        typeof entry.user_name === 'string' && entry.user_name.length > 0,
    )
    .map((entry, index) => ({
      rank: index + 1,
      user_id: entry.user_id,
      user_name: entry.user_name,
      high_score: entry.high_score,
    }));
}
